import type { DashboardPoint } from "./types";
import { DISPLAY_LOCALE } from "../format/dates";

/*
 * Turning a list of timestamps into chart points, for the home dashboards. Every bucket is in the reader's own time
 * zone, which is what "this month" means to the person looking.
 */

export interface Bucket {
  readonly start: Date;
  readonly end: Date;
  readonly label: string;
}

/** The last `n` calendar months, oldest first, the current month last. */
export function lastMonths(n: number, now: Date): Bucket[] {
  const out: Bucket[] = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
    out.push({ start, end, label: start.toLocaleDateString(DISPLAY_LOCALE, { month: "short" }) });
  }
  return out;
}

/** The last `n` days, oldest first, today last. */
export function lastDays(n: number, now: Date): Bucket[] {
  const out: Bucket[] = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i + 1);
    out.push({
      start,
      end,
      label: start.toLocaleDateString(DISPLAY_LOCALE, { day: "numeric", month: "short" }),
    });
  }
  return out;
}

/** The next `n` weeks from today, each starting on today's weekday. */
export function nextWeeks(n: number, now: Date): Bucket[] {
  const out: Bucket[] = [];
  for (let i = 0; i < n; i += 1) {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i * 7);
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (i + 1) * 7);
    out.push({
      start,
      end,
      label: start.toLocaleDateString(DISPLAY_LOCALE, { day: "numeric", month: "short" }),
    });
  }
  return out;
}

/** How many of the timestamps fall in each bucket. */
export function countInto(
  buckets: readonly Bucket[],
  isoDates: readonly string[],
): DashboardPoint[] {
  const times = isoDates.map((d) => new Date(d).getTime());
  return buckets.map((b) => ({
    label: b.label,
    value: times.filter((t) => t >= b.start.getTime() && t < b.end.getTime()).length,
  }));
}

/**
 * A running total ending at `total`: how many there were at the end of each bucket, counting back from today by what
 * was added after it. Exact for things that are only ever added; an approximation for things also removed.
 */
export function runningTotal(added: readonly DashboardPoint[], total: number): number[] {
  const out: number[] = [];
  let after = 0;
  for (let i = added.length - 1; i >= 0; i -= 1) {
    out.unshift(total - after);
    after += added[i]?.value ?? 0;
  }
  return out;
}
